import { useCallback, useState } from "react";
import type { U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import type { U3130ApproveBatchBody, U3130ApproveBatchResult } from "./U3130Service";

export interface UseU3130ApproveBatchHandlersProps {
  doApproveBatchFn: (body: U3130ApproveBatchBody) => Promise<U3130ApproveBatchResult>;
  onError: (params: { error: unknown }) => void;
}

export function useU3130ApproveBatchHandlers({ doApproveBatchFn, onError }: UseU3130ApproveBatchHandlersProps) {
  const [isRunning, setIsRunning] = useState(false);

  const previewBatch = useCallback(
    async (filter: U2000SearchParameter): Promise<U3130ApproveBatchResult | null> => {
      setIsRunning(true);
      try {
        return await doApproveBatchFn({ filter, dryRun: true });
      } catch (error: unknown) {
        onError({ error });
        return null;
      } finally {
        setIsRunning(false);
      }
    },
    [doApproveBatchFn, onError],
  );

  const executeBatch = useCallback(
    async (filter: U2000SearchParameter): Promise<U3130ApproveBatchResult | null> => {
      setIsRunning(true);
      try {
        return await doApproveBatchFn({ filter, dryRun: false });
      } catch (error: unknown) {
        onError({ error });
        return null;
      } finally {
        setIsRunning(false);
      }
    },
    [doApproveBatchFn, onError],
  );

  return { previewBatch, executeBatch, isRunning };
}
